/**
 * SessionsPage - Session Management
 *
 * Primary operational view showing:
 * - Unified session list with overdue indicators
 * - Slim overdue alert banner when applicable
 * - Session history (tab)
 */

import { useState } from 'react';
import {
  Box,
  Button,
  Group,
  Modal,
  SegmentedControl,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useActiveSessions, useSessionHistory, useEndSession } from '@commander/api-client';
import { IconSearch, IconStop, IconClock, IconMapPin } from '@/components/icons';
import { PanelHeader } from '@/components/PanelHeader';

/* ====================================
   CSS Keyframe Animations (injected once)
   ==================================== */
const sessionsKeyframes = `
  @keyframes sessRowSlideIn {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes sessPageFadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

// Inject keyframes into document head (only once)
if (typeof document !== 'undefined') {
  const styleId = 'sessions-keyframes';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = sessionsKeyframes;
    document.head.appendChild(style);
  }
}

/** Inline alert-triangle icon for the overdue banner */
function IconAlertTriangle({ size = 16 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

interface Session {
  id: string;
  equipment: { tagId: string; description: string };
  user: { username: string };
  location: { name: string };
  checkedOutAt: string;
  expectedReturnAt: string | null;
  isOverdue: boolean;
}

/** Shared column header style to avoid repetition */
const columnHeaderStyle = {
  fontSize: '0.7rem',
  fontWeight: 500 as const,
  color: 'var(--text-muted)',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.08em',
};

/** Calculate duration string */
function formatDuration(startDate: string): string {
  const start = new Date(startDate);
  const now = new Date();
  const diffMs = now.getTime() - start.getTime();
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

/** Calculate how long a session is overdue */
function formatOverdue(expectedReturn: string): string | null {
  const expected = new Date(expectedReturn);
  const now = new Date();
  const diffMs = now.getTime() - expected.getTime();
  if (diffMs <= 0) return null;
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) return `${hours}h ${minutes}m overdue`;
  return `${minutes}m overdue`;
}

/** Inline radio/walkie-talkie icon for the modal detail card */
function IconRadio({ size = 16 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="6" y="5" width="12" height="17" rx="2" />
      <circle cx="12" cy="13" r="3" />
      <line x1="12" y1="5" x2="18" y2="2" />
    </svg>
  );
}

/** Inline user icon for the modal detail card */
function IconUser({ size = 16 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

/** Session row component */
function SessionRow({
  session,
  onEndSession,
  rowIndex = 0,
}: {
  session: Session;
  onEndSession: (session: Session) => void;
  rowIndex?: number;
}) {
  const duration = formatDuration(session.checkedOutAt);
  const expectedReturn = session.expectedReturnAt ? new Date(session.expectedReturnAt) : null;
  const isOverdue = session.isOverdue;

  return (
    <Box
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr 100px 100px auto',
        gap: 16,
        alignItems: 'center',
        padding: '0.75rem 1rem',
        borderBottom: '1px solid var(--border-colour)',
        transition: 'background-color 150ms ease, box-shadow 200ms ease',
        animation: `sessRowSlideIn 350ms ease-out ${rowIndex * 40}ms backwards`,
        boxShadow: 'inset 0 0 0 0 transparent',
        /* Subtle warm tint for overdue rows -- just enough to distinguish without shouting */
        backgroundColor: isOverdue ? 'rgba(220, 38, 38, 0.03)' : 'transparent',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--bg-base)';
        if (!isOverdue) {
          e.currentTarget.style.boxShadow = 'inset 3px 0 0 0 #3B82F6';
        } else {
          e.currentTarget.style.boxShadow = 'inset 3px 0 0 0 #dc2626';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = isOverdue
          ? 'rgba(220, 38, 38, 0.03)'
          : 'transparent';
        e.currentTarget.style.boxShadow = 'inset 0 0 0 0 transparent';
      }}
    >
      {/* Equipment */}
      <Box>
        <Text
          ff="monospace"
          style={{
            fontSize: '0.875rem',
            fontWeight: 400,
            color: 'var(--text-primary)',
          }}
        >
          {session.equipment.tagId}
        </Text>
        <Text style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
          {session.equipment.description}
        </Text>
      </Box>

      {/* User */}
      <Text style={{ fontSize: '0.875rem', fontWeight: 400, color: 'var(--text-primary)' }}>
        {session.user.username}
      </Text>

      {/* Location */}
      <Text style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
        {session.location.name}
      </Text>

      {/* Duration */}
      <Text
        ff="monospace"
        style={{
          fontSize: '0.875rem',
          fontWeight: 400,
          color: isOverdue ? '#dc2626' : 'var(--text-secondary)',
        }}
      >
        {duration}
      </Text>

      {/* Expected return */}
      <Text
        ff="monospace"
        style={{
          fontSize: '0.8125rem',
          color: isOverdue ? '#dc2626' : 'var(--text-muted)',
        }}
      >
        {expectedReturn?.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) ?? '—'}
      </Text>

      {/* Actions -- contextual styling: understated for normal, prominent for overdue */}
      <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          size="compact-xs"
          variant={isOverdue ? 'light' : 'subtle'}
          color={isOverdue ? 'red' : 'gray'}
          leftSection={<IconStop size={12} />}
          onClick={() => onEndSession(session)}
          styles={{
            root: {
              fontSize: '0.75rem',
              fontWeight: 500,
              height: 28,
              paddingLeft: 10,
              paddingRight: 12,
              borderRadius: 4,
              /* Smooth press feedback via scale transform */
              transition: 'transform 100ms ease, background-color 150ms ease, color 150ms ease',
              '&:active': {
                transform: 'scale(0.94)',
              },
            },
          }}
        >
          End
        </Button>
      </Box>
    </Box>
  );
}

/** History row component */
function HistoryRow({
  session,
  rowIndex = 0,
}: {
  session: {
    id: string;
    equipment: { tagId: string };
    user: { username: string };
    checkoutLocation: { name: string };
    checkinLocation: { name: string } | null;
    checkedOutAt: string;
    checkedInAt: string | null;
    wasDefective: boolean;
  };
  rowIndex?: number;
}) {
  return (
    <Box
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr 1fr 100px',
        gap: 16,
        alignItems: 'center',
        padding: '0.75rem 1rem',
        borderBottom: '1px solid var(--border-colour)',
        transition: 'background-color 150ms ease, box-shadow 200ms ease',
        animation: `sessRowSlideIn 350ms ease-out ${rowIndex * 40}ms backwards`,
        boxShadow: 'inset 0 0 0 0 transparent',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--bg-base)';
        e.currentTarget.style.boxShadow = 'inset 3px 0 0 0 #3B82F6';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
        e.currentTarget.style.boxShadow = 'inset 0 0 0 0 transparent';
      }}
    >
      <Text ff="monospace" style={{ fontSize: '0.875rem', fontWeight: 400, color: 'var(--text-secondary)' }}>
        {session.equipment.tagId}
      </Text>
      <Text style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
        {session.user.username}
      </Text>
      <Text style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
        {session.checkoutLocation.name}
      </Text>
      <Text style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
        {session.checkedInAt
          ? new Date(session.checkedInAt).toLocaleString('en-GB')
          : '\u2014'}
      </Text>
      <Text style={{ fontSize: '0.75rem', color: session.wasDefective ? 'var(--amber-accent)' : 'var(--text-muted)' }}>
        {session.wasDefective ? 'Defective' : '\u2014'}
      </Text>
    </Box>
  );
}

export function SessionsPage() {
  const [view, setView] = useState<'active' | 'history'>('active');
  const [search, setSearch] = useState('');
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);
  const [endingSession, setEndingSession] = useState<Session | null>(null);

  const { data: activeSessions, isLoading: loadingActive } = useActiveSessions();
  const { data: historySessions, isLoading: loadingHistory } = useSessionHistory();
  const endSession = useEndSession();

  // Filter active sessions
  const filteredSessions = activeSessions?.items.filter((session) => {
    if (showOverdueOnly && !session.isOverdue) return false;
    if (search) {
      const searchLower = search.toLowerCase();
      return (
        session.equipment.tagId.toLowerCase().includes(searchLower) ||
        session.user.username.toLowerCase().includes(searchLower) ||
        session.location.name.toLowerCase().includes(searchLower)
      );
    }
    return true;
  }) ?? [];

  // Separate overdue sessions (shown first in the unified list)
  const overdueSessions = filteredSessions.filter((s) => s.isOverdue);
  const normalSessions = filteredSessions.filter((s) => !s.isOverdue);
  const allSessions = [...overdueSessions, ...normalSessions];

  const handleEndSession = async () => {
    if (!endingSession) return;

    try {
      await endSession.mutateAsync({ sessionId: endingSession.id });
      notifications.show({
        title: 'Session Ended',
        message: `Session for ${endingSession.equipment.tagId} has been terminated.`,
        color: 'green',
      });
      setEndingSession(null);
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to end session. Please try again.',
        color: 'red',
      });
    }
  };

  return (
    <Stack gap="lg" style={{ animation: 'sessPageFadeIn 400ms ease-out' }}>
      {/* Page header - matches Equipment/Users pattern */}
      <Group justify="space-between" align="flex-end">
        <Box>
          <Text
            style={{
              fontSize: '1.75rem',
              fontWeight: 500,
              letterSpacing: '-0.01em',
              color: 'var(--text-primary)',
            }}
          >
            Sessions
          </Text>
          <Text
            style={{
              fontSize: '0.875rem',
              fontWeight: 400,
              color: 'var(--text-muted)',
              marginTop: 4,
            }}
          >
            {view === 'active'
              ? `${activeSessions?.items.length ?? 0} active sessions`
              : 'Session history'}
          </Text>
        </Box>

        <SegmentedControl
          value={view}
          onChange={(v) => setView(v as 'active' | 'history')}
          data={[
            { label: 'Active', value: 'active' },
            { label: 'History', value: 'history' },
          ]}
          styles={{
            root: {
              backgroundColor: 'var(--bg-card)',
              border: '1px solid var(--border-colour)',
            },
            label: {
              color: 'var(--text-muted)',
              fontSize: '0.8125rem',
              fontWeight: 500,
            },
            indicator: {
              backgroundColor: 'var(--bg-base)',
            },
            control: {
              borderColor: 'transparent',
            },
          }}
        />
      </Group>

      {/* Active view */}
      {view === 'active' && (
        <Box
          style={{
            backgroundColor: 'var(--bg-card)',
            border: '1px solid var(--border-colour)',
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          {/* Panel header - consistent with Equipment/Users pages */}
          <PanelHeader title="Active Sessions" accentColour="#1e293b">
            <Group gap="sm">
              {overdueSessions.length > 0 && (
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '4px 10px',
                    borderRadius: 4,
                    backgroundColor: 'rgba(220, 38, 38, 0.08)',
                  }}
                >
                  <Box style={{ color: '#dc2626', display: 'flex', alignItems: 'center' }}>
                    <IconAlertTriangle size={12} />
                  </Box>
                  <Text
                    style={{
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      color: '#dc2626',
                    }}
                  >
                    {overdueSessions.length} overdue
                  </Text>
                </Box>
              )}
              <Switch
                label="Overdue only"
                checked={showOverdueOnly}
                onChange={(e) => setShowOverdueOnly(e.currentTarget.checked)}
                size="sm"
                color="red"
                styles={{
                  label: {
                    fontSize: '0.8125rem',
                    fontWeight: 400,
                    color: 'var(--text-secondary)',
                  },
                }}
              />
            </Group>
          </PanelHeader>

          {/* Search bar */}
          <Box
            style={{
              padding: '1rem',
              borderBottom: '1px solid var(--border-colour)',
              background: 'var(--bg-base)',
            }}
          >
            <TextInput
              placeholder="Search by equipment, user, or location..."
              leftSection={<IconSearch size={16} style={{ color: 'var(--text-muted)' }} />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              styles={{
                input: {
                  backgroundColor: 'var(--bg-card)',
                  border: '1px solid var(--border-colour)',
                  color: 'var(--text-primary)',
                  '&::placeholder': { color: 'var(--text-muted)' },
                  '&:focus': { borderColor: 'var(--blue-accent)' },
                },
              }}
              style={{ maxWidth: 400 }}
            />
          </Box>

          {/* Column headers - shown once for the unified table */}
          <Box
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr 100px 100px auto',
              gap: 16,
              padding: '10px 1rem',
              borderBottom: '1px solid var(--border-colour)',
              backgroundColor: 'var(--bg-base)',
            }}
          >
            <Text style={columnHeaderStyle}>Equipment</Text>
            <Text style={columnHeaderStyle}>User</Text>
            <Text style={columnHeaderStyle}>Location</Text>
            <Text style={columnHeaderStyle}>Duration</Text>
            <Text style={columnHeaderStyle}>Due</Text>
            <Text style={{ ...columnHeaderStyle, textAlign: 'right' }}>Actions</Text>
          </Box>

          {/* Unified session rows - overdue first, then active */}
          {loadingActive ? (
            <Box style={{ padding: '48px', textAlign: 'center' }}>
              <Text style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                Loading sessions...
              </Text>
            </Box>
          ) : allSessions.length === 0 ? (
            <Box style={{ padding: '48px', textAlign: 'center' }}>
              <Text style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                {search || showOverdueOnly ? 'No sessions match your filters.' : 'No active sessions.'}
              </Text>
            </Box>
          ) : (
            allSessions.map((session, rowIndex) => (
              <SessionRow
                key={session.id}
                session={session}
                onEndSession={setEndingSession}
                rowIndex={rowIndex}
              />
            ))
          )}

          {/* Record count footer */}
          {allSessions.length > 0 && (
            <Box
              style={{
                padding: '0.75rem 1rem',
                borderTop: '1px solid var(--border-colour)',
              }}
            >
              <Text size="xs" c="dimmed" ff="monospace">
                Showing {allSessions.length} of {activeSessions?.items.length ?? 0} sessions
              </Text>
            </Box>
          )}
        </Box>
      )}

      {/* History view */}
      {view === 'history' && (
        <Box
          style={{
            backgroundColor: 'var(--bg-card)',
            border: '1px solid var(--border-colour)',
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          <PanelHeader title="Session History" accentColour="#1e293b" />

          {/* Column headers */}
          <Box
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr 1fr 100px',
              gap: 16,
              padding: '10px 1rem',
              borderBottom: '1px solid var(--border-colour)',
              backgroundColor: 'var(--bg-base)',
            }}
          >
            <Text style={columnHeaderStyle}>Equipment</Text>
            <Text style={columnHeaderStyle}>User</Text>
            <Text style={columnHeaderStyle}>Location</Text>
            <Text style={columnHeaderStyle}>Checked In</Text>
            <Text style={columnHeaderStyle}>Status</Text>
          </Box>

          {loadingHistory ? (
            <Box style={{ padding: '48px', textAlign: 'center' }}>
              <Text style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Loading history...</Text>
            </Box>
          ) : historySessions?.items.length === 0 ? (
            <Box style={{ padding: '48px', textAlign: 'center' }}>
              <Text style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>No session history.</Text>
            </Box>
          ) : (
            historySessions?.items.map((session, rowIndex) => (
              <HistoryRow key={session.id} session={session} rowIndex={rowIndex} />
            ))
          )}

          {/* Record count footer */}
          {(historySessions?.items.length ?? 0) > 0 && (
            <Box
              style={{
                padding: '0.75rem 1rem',
                borderTop: '1px solid var(--border-colour)',
              }}
            >
              <Text size="xs" c="dimmed" ff="monospace">
                Showing {historySessions?.items.length ?? 0} records
              </Text>
            </Box>
          )}
        </Box>
      )}

      {/* End session confirmation modal -- premium redesign */}
      <Modal
        opened={!!endingSession}
        onClose={() => setEndingSession(null)}
        centered
        withCloseButton={false}
        padding={0}
        radius={12}
        overlayProps={{ backgroundOpacity: 0.35, blur: 4 }}
        styles={{
          content: {
            backgroundColor: 'var(--bg-card)',
            border: '1px solid var(--border-colour)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(0, 0, 0, 0.08)',
            overflow: 'hidden',
          },
          body: { padding: 0 },
        }}
      >
        {endingSession && (() => {
          const isOverdue = endingSession.isOverdue;
          const overdueText = endingSession.expectedReturnAt ? formatOverdue(endingSession.expectedReturnAt) : null;

          return (
            <Stack gap={0}>
              {/* Header area with icon and title */}
              <Box
                style={{
                  padding: '24px 24px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                {/* Warning icon with coloured background circle */}
                <Box
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    backgroundColor: isOverdue
                      ? 'rgba(220, 38, 38, 0.1)'
                      : 'rgba(245, 158, 11, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isOverdue ? '#dc2626' : '#f59e0b',
                  }}
                >
                  <IconAlertTriangle size={22} />
                </Box>

                <Text
                  style={{
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    textAlign: 'center',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {isOverdue ? 'End Overdue Session' : 'End Session'}
                </Text>

                <Text
                  style={{
                    fontSize: '0.8125rem',
                    color: 'var(--text-muted)',
                    textAlign: 'center',
                    lineHeight: 1.5,
                  }}
                >
                  {isOverdue
                    ? 'This session has exceeded its expected return time. End it to return the equipment to available status.'
                    : 'Return this equipment to available status?'}
                </Text>
              </Box>

              {/* Session details card */}
              <Box
                style={{
                  margin: '0 24px',
                  padding: '14px 16px',
                  backgroundColor: 'var(--bg-base)',
                  borderRadius: 8,
                  border: '1px solid var(--border-colour)',
                }}
              >
                <Stack gap={10}>
                  {/* Equipment row */}
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Box
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 6,
                        backgroundColor: 'rgba(59, 130, 246, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#3b82f6',
                        flexShrink: 0,
                      }}
                    >
                      <IconRadio size={14} />
                    </Box>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Text style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500, marginBottom: 1 }}>
                        Equipment
                      </Text>
                      <Text ff="monospace" style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                        {endingSession.equipment.tagId}
                      </Text>
                    </Box>
                  </Box>

                  {/* Subtle divider */}
                  <Box style={{ height: 1, backgroundColor: 'var(--border-colour)', opacity: 0.6 }} />

                  {/* User row */}
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Box
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 6,
                        backgroundColor: 'rgba(99, 102, 241, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#6366f1',
                        flexShrink: 0,
                      }}
                    >
                      <IconUser size={14} />
                    </Box>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Text style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500, marginBottom: 1 }}>
                        User
                      </Text>
                      <Text style={{ fontSize: '0.8125rem', fontWeight: 400, color: 'var(--text-primary)' }}>
                        {endingSession.user.username}
                      </Text>
                    </Box>
                  </Box>

                  {/* Subtle divider */}
                  <Box style={{ height: 1, backgroundColor: 'var(--border-colour)', opacity: 0.6 }} />

                  {/* Location row */}
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Box
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 6,
                        backgroundColor: 'rgba(16, 185, 129, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#10b981',
                        flexShrink: 0,
                      }}
                    >
                      <IconMapPin size={14} />
                    </Box>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Text style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500, marginBottom: 1 }}>
                        Location
                      </Text>
                      <Text style={{ fontSize: '0.8125rem', fontWeight: 400, color: 'var(--text-primary)' }}>
                        {endingSession.location.name}
                      </Text>
                    </Box>
                  </Box>

                  {/* Subtle divider */}
                  <Box style={{ height: 1, backgroundColor: 'var(--border-colour)', opacity: 0.6 }} />

                  {/* Duration row */}
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Box
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 6,
                        backgroundColor: isOverdue
                          ? 'rgba(220, 38, 38, 0.08)'
                          : 'rgba(245, 158, 11, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: isOverdue ? '#dc2626' : '#f59e0b',
                        flexShrink: 0,
                      }}
                    >
                      <IconClock size={14} />
                    </Box>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Text style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500, marginBottom: 1 }}>
                        Duration
                      </Text>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Text
                          ff="monospace"
                          style={{
                            fontSize: '0.8125rem',
                            fontWeight: 500,
                            color: isOverdue ? '#dc2626' : 'var(--text-primary)',
                          }}
                        >
                          {formatDuration(endingSession.checkedOutAt)}
                        </Text>
                        {isOverdue && overdueText && (
                          <Text
                            style={{
                              fontSize: '0.6875rem',
                              fontWeight: 600,
                              color: '#dc2626',
                              backgroundColor: 'rgba(220, 38, 38, 0.08)',
                              padding: '2px 6px',
                              borderRadius: 4,
                            }}
                          >
                            {overdueText}
                          </Text>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Stack>
              </Box>

              {/* Button area */}
              <Box
                style={{
                  padding: '20px 24px 24px',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 8,
                }}
              >
                <Button
                  variant="subtle"
                  color="gray"
                  onClick={() => setEndingSession(null)}
                  radius={8}
                  styles={{
                    root: {
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      height: 38,
                      paddingLeft: 20,
                      paddingRight: 20,
                      color: 'var(--text-secondary)',
                      transition: 'transform 100ms ease, background-color 150ms ease',
                    },
                  }}
                  onMouseDown={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.96)';
                  }}
                  onMouseUp={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="filled"
                  color="red"
                  onClick={handleEndSession}
                  loading={endSession.isPending}
                  leftSection={<IconStop size={15} />}
                  radius={8}
                  styles={{
                    root: {
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      height: 38,
                      paddingLeft: 18,
                      paddingRight: 20,
                      transition: 'transform 100ms ease, background-color 150ms ease, box-shadow 150ms ease',
                      boxShadow: '0 1px 3px rgba(220, 38, 38, 0.2)',
                    },
                  }}
                  onMouseDown={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.96)';
                  }}
                  onMouseUp={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
                  }}
                >
                  End Session
                </Button>
              </Box>
            </Stack>
          );
        })()}
      </Modal>
    </Stack>
  );
}
